import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Theme, Typography, useTheme } from "@mui/material";
import { SystemStyleObject } from "@mui/system";
import { useTenantTranslation } from "hooks/formatters";
import React from "react";
import { LoadBasketLayerRead } from "src/store/api/generatedApi";

interface Props {
  layers: LoadBasketLayerRead[];
  completed: boolean;
  size: "big" | "medium";
  sx?: SystemStyleObject<Theme>;
  mixQuantity?: "mass" | "grabs";
}

const getOpacity = (faded: boolean) => (faded ? 0.5 : 1);

interface BasketLabelProps {
  children: string;
  faded: boolean;
  size: "big" | "medium";
}

const getTypographyVariant = (size: "big" | "medium") => {
  switch (size) {
    case "big":
      return "h1";
    case "medium":
      return "h5";
  }
};

const getPadding = (size: "big" | "medium") => {
  switch (size) {
    case "big":
      return 2;
    case "medium":
      return 0.5;
  }
};

const BasketLabel = ({ children, faded, size }: BasketLabelProps) => {
  const theme = useTheme();
  return (
    <Typography
      variant={getTypographyVariant(size)}
      sx={{
        backgroundColor: theme.palette.grey[50],
        borderColor: theme.palette.grey[200],
        borderStyle: "solid",
        borderWidth: 1,
        padding: getPadding(size),
        opacity: getOpacity(faded),
      }}
    >
      {children}
    </Typography>
  );
};

const getColumnWidth = (size: "big" | "medium"): 200 | 100 => {
  switch (size) {
    case "big":
      return 200;
    case "medium":
      return 100;
  }
};

const SequenceLabel = ({
  label,
  size,
}: {
  label: string;
  size: "big" | "medium";
}) => (
  <Box sx={{ gridColumn: "1/-1", display: "grid", placeItems: "center" }}>
    <Typography
      sx={{
        color: "text.disabled",
        textTransform: "uppercase",
        letterSpacing: 0.5,
        fontWeight: "bold",
        fontSize: size === "big" ? 20 : 12,
      }}
    >
      {label}
    </Typography>
  </Box>
);

export const Basket = ({ layers, completed, size, sx, mixQuantity }: Props) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  return (
    <Box
      sx={[
        ...(sx ? [sx] : []),
        {
          display: "grid",
          position: "relative",
          gridTemplateColumns: `repeat(2, ${getColumnWidth(size)}px)`,
          gridAutoRows: `max-content`,
          gap: 1,
        },
      ]}
    >
      {completed ? (
        <CheckCircleIcon
          sx={{
            position: "absolute",
            top: "40%",
            left: "50%",
            zIndex: 2,
            transform: "translate(-50%, -50%)",
            fontSize: 84,
          }}
          color="success"
        />
      ) : null}
      {layers.length > 0 && (
        <SequenceLabel label={t("bottomFirst")} size={size} />
      )}
      {layers
        .slice()
        .sort((a, b) => {
          if (a.quantity.unit === "grab" && b.quantity.unit === "mass")
            return -1;
          if (a.quantity.unit === "mass" && b.quantity.unit === "grab")
            return 1;
          return 0;
        })
        .map((layer) => {
          const shouldShowPlus = layer.quantity.unit === "grab";
          const massDisplay = shouldShowPlus
            ? `+${layer.quantity.value.toFixed(0)}`
            : layer.quantity.value.toFixed(0);

          return (
            <React.Fragment key={layer.layer_number}>
              <BasketLabel faded={completed} size={size}>
                {layer.material_name}
              </BasketLabel>
              <BasketLabel faded={completed} size={size}>
                {massDisplay}
              </BasketLabel>
            </React.Fragment>
          );
        })}
      {layers.length > 0 && <SequenceLabel label={t("topLast")} size={size} />}
      {mixQuantity !== "grabs" && (
        <Box
          sx={{
            gridColumn: "1/-1",
            height: 50,
            display: "grid",
            placeItems: "center",
            opacity: getOpacity(completed),
          }}
        >
          <Typography
            variant={getTypographyVariant(size)}
            sx={{ color: theme.palette.text.secondary }}
          >
            {t("totalMass")}
            {": "}
            {layers
              .reduce((acc, layer) => acc + Math.round(layer.quantity.value), 0)
              .toFixed()}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
